import type { Component } from 'vue';

type TransitionResult =
  | string
  | {
      submit?: boolean;
      customSubmit?: boolean;
      redirect?: string;
      action?: () => void | Promise<void>;
    };

export interface FlowStep {
  type: 'form' | 'component' | 'end';
  title?: string;
  description?: string;
  component?: Component;
  schema?: Record<string, any>;
  props?:
    | Record<string, any>
    | ((ctx: { flowData: Record<string, any>; request: any }) => Record<string, any>);
  buttonNextTitle?: string;
  isButtonNextDisabled?: boolean;
  header?: string;
  id?: string;
}

export interface FlowTransition {
  next?: string | ((ctx: { data: Record<string, any> }) => TransitionResult);
  submit?: boolean;
  success?: string;
  error?: string;
  back?: string;
  guard?: (data: Record<string, any>) => boolean;
  skip?: (data: Record<string, any>) => boolean;
  skipTo?: string;
  sideEffect?: (ctx: { data: Record<string, any> }) => Promise<void>;
}

export interface Flow {
  id: string;
  basePath: string;
  exitRoute?: string;
  steps: Record<string, FlowStep>;
  transitions: Record<string, FlowTransition>;
  onInit?: (ctx: { route: any }) => Promise<Record<string, any>>;
  onSubmit?: (data: Record<string, any>) => Promise<void>;
  successRedirect?: (data: Record<string, any>) => string;
  // Rejoue `onInit` à chaque entrée de vue au lieu de servir le cache flowStore.
  // Nécessaire pour le labo : le VRF doit repartir de la DERNIÈRE visite/patient
  // en base (cahier Réf 19), le snapshot localStorage pouvant être périmé.
  refetchOnEnter?: boolean;
}

export const globalFlowSteps: Record<string, FlowStep> = {
  end: {
    type: 'end',
    title: 'Compte rendu envoyé',
    description: 'Merci pour votre engagement.',
  },
  'bad-end': {
    type: 'end',
    title: 'Une erreur est survenue',
    description: 'Veuillez réessayer plus tard.',
  },
};

export function withGlobalFlow(
  flow: Omit<Flow, 'steps' | 'transitions'> & {
    steps: Record<string, Partial<FlowStep>>;
    transitions: Record<string, FlowTransition>;
  },
): Flow {
  return {
    ...flow,
    steps: {
      ...flow.steps,
      ...globalFlowSteps,
    },
    transitions: {
      ...flow.transitions,
      ...Object.fromEntries(
        Object.entries(flow.transitions || {})
          .filter(([_, config]) => config.submit)
          .map(([key, config]) => [
            key,
            {
              ...config,
              success: config.success || 'end',
              error: config.error || 'bad-end',
            },
          ]),
      ),
    },
  };
}
